import React, { useState, useEffect, useMemo } from "react"
import {
  Container, Row, Col, Card, CardBody, Table, Button, Nav, NavItem, NavLink,
  Badge, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup,
} from "reactstrap"
import { withRouter } from "react-router-dom"
import { connect } from "react-redux"
import classnames from "classnames"
import moment from "moment"
import _ from "lodash"
import Flatpickr1 from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"

import * as backend from "../../helpers/backend_helper"
import Toaster from "../../components/Common/Toaster"

const Flatpickr: any = Flatpickr1

const STATUS_OPTIONS = ["Pending", "In-Progress", "Completed", "Cancelled"]
// Status the assignee may pick (Cancelled is an assigner-only action).
const ASSIGNEE_STATUS = ["Pending", "In-Progress", "Completed"]

const fmtDT = (d: any) => (d ? moment(d).format("DD/MM/YYYY, HH:mm") : "—")

// Early / On-time / Delay colour coding for the submission timing.
const TIMING: { [k: string]: { label: string; color: string } } = {
  early: { label: "EARLY", color: "#0d6efd" },
  ontime: { label: "ON-TIME", color: "#1c8577" },
  delay: { label: "DELAY", color: "#c0392b" },
}

const StatusPill = ({ status }: { status: string }) => {
  const map: any = {
    "Pending": { fg: "#b7791f", bg: "#fdf3e3", bd: "#e0a83c" },
    "In-Progress": { fg: "#0d6efd", bg: "#e7f0ff", bd: "#7fb0ff" },
    "Completed": { fg: "#1c8577", bg: "#e5f6f2", bd: "#2ea99b" },
    "Cancelled": { fg: "#6b7785", bg: "#eef1f4", bd: "#cdd5dd" },
  }
  const c = map[status] || map["Pending"]
  return (
    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 12, fontWeight: 700, fontSize: 12, color: c.fg, background: c.bg, border: `1px solid ${c.bd}` }}>
      {status || "Pending"}
    </span>
  )
}

// NOTE: this MUST stay at module scope. Defining it inside TasksAssigned makes it
// a new component type on every render, so React remounts the subtree and leaves
// the rendered ✓/✗ DOM nodes pointing at stale fibers — the delegated onClick then
// never fires and the buttons become unclickable. (MW.pptx: "Unable to select Check box".)
const CheckButtons = ({ task, interactive, onApprove, onReject }: {
  task: any; interactive: boolean
  onApprove?: (t: any) => void; onReject?: (t: any) => void
}) => {
  const mark = task.check && task.check.mark
  if (!task.submitted) return <span style={{ color: "#c3ccd6" }}>—</span>
  if (interactive) {
    return (
      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
        <button type="button" title="Approve (move to Completed)" onClick={() => onApprove && onApprove(task)}
          style={{ border: `1px solid ${mark === "yes" ? "#2ea99b" : "#cdd5dd"}`, background: mark === "yes" ? "#2ea99b" : "#fff", color: mark === "yes" ? "#fff" : "#6b7785", borderRadius: 6, width: 30, height: 28, cursor: "pointer", fontWeight: 700 }}>✓</button>
        <button type="button" title="Send back to the assignee" onClick={() => onReject && onReject(task)}
          style={{ border: "1px solid #cdd5dd", background: "#fff", color: "#6b7785", borderRadius: 6, width: 30, height: 28, cursor: "pointer", fontWeight: 700 }}>✗</button>
      </div>
    )
  }
  // read-only (assignee's view): show the assigner's mark
  if (mark === "yes") return <span style={{ color: "#1c8577", fontWeight: 700 }}>✓</span>
  if (mark === "no") return <span style={{ color: "#c0392b", fontWeight: 700 }}>✗</span>
  return <span style={{ color: "#c3ccd6" }}>—</span>
}

const TasksAssigned = (props: any) => {
  const { selectedFactory, user } = props
  const orgId = selectedFactory && selectedFactory.orgId
  const facId = selectedFactory && selectedFactory._id
  const myId = user && String(user._id)

  const [tasks, setTasks] = useState<any[]>([])
  const [userList, setUserList] = useState<any[]>([])
  const [tab, setTab] = useState<"to" | "by">("to")
  const [showCompletedTo, setShowCompletedTo] = useState(false)
  const [showCompletedBy, setShowCompletedBy] = useState(false)

  // Create-task modal
  const [createOpen, setCreateOpen] = useState(false)
  const [newTitle, setNewTitle] = useState("")
  const [newAssignee, setNewAssignee] = useState("")
  const [newTarget, setNewTarget] = useState<any>(null)
  const [saving, setSaving] = useState(false)

  // Reject-task modal (captures the required "reason for rejection")
  const [rejectTask, setRejectTask] = useState<any>(null)
  const [rejectReason, setRejectReason] = useState("")
  const [rejecting, setRejecting] = useState(false)

  // Completed-tasks PDF date range (per window)
  const [compRange, setCompRange] = useState<string[]>([
    moment().startOf("month").format("YYYY-MM-DD"),
    moment().format("YYYY-MM-DD"),
  ])

  const loadTasks = () => {
    if (!orgId || !facId) return
    backend.getTasks(orgId, facId).then((d: any) => setTasks(d || [])).catch(() => setTasks([]))
  }

  useEffect(() => {
    loadTasks()
    if (orgId) backend.getUserOrg(orgId).then(setUserList).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, facId])

  // Access level for the current user on this factory.
  const perms: string[] = (selectedFactory && selectedFactory.permission) || []
  const isEditor = perms.includes("ADMIN") || perms.includes("EDIT")

  // Can the current user create (assign) tasks, and to whom?
  const myTaskConfig = ((selectedFactory && selectedFactory.taskConfig) || []).find(
    (c: any) => String(c.userId) === myId
  )
  const canAssign = isEditor || !!(myTaskConfig && myTaskConfig.canAssign)

  const activeTeamIds: string[] = useMemo(
    () => ((selectedFactory && selectedFactory.teams) || [])
      .filter((t: any) => t.status !== false)
      .map((t: any) => String(t.userId)),
    [selectedFactory]
  )

  const nameOf = (id: any) => {
    const u = _.find(userList, ["_id", String(id)])
    return u ? u.name : ""
  }

  // Users the current user may assign tasks to.
  const assignableUsers = useMemo(() => {
    let ids: string[] = []
    if (isEditor) ids = activeTeamIds
    else if (myTaskConfig) ids = (myTaskConfig.users || []).map(String)
    // A user can never assign a task to themselves.
    ids = ids.filter((id) => id !== myId)
    return ids
      .map((id) => ({ value: id, label: `${nameOf(id)}` }))
      .filter((o) => o.label)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditor, activeTeamIds, myTaskConfig, userList, myId])

  const assignedToMe = tasks.filter((t) => String(t.assignedTo) === myId)
  const assignedByMe = tasks.filter((t) => String(t.assignedBy) === myId)

  const openOf = (list: any[]) => list.filter((t) => !t.submitted)
  const doneOf = (list: any[]) => list.filter((t) => t.submitted)
  // A submitted task is "approved" once the assigner ticks ✓ (check.mark === "yes").
  const isApproved = (t: any) => !!(t.check && t.check.mark === "yes")

  const pendingToCount = assignedToMe.filter((t) => !t.submitted && t.status !== "Cancelled").length
  // Assigner's badge = tasks submitted and awaiting their ✓/✗ review.
  const pendingByCount = assignedByMe.filter((t) => t.submitted && !isApproved(t)).length

  // ---- actions ----
  const onStatusChange = async (task: any, status: string) => {
    try {
      await backend.updateTaskStatus(orgId, facId, task._id, status)
      setTasks((ts) => ts.map((t) => (t._id === task._id ? { ...t, status } : t)))
    } catch (e) {
      Toaster.error(typeof e === "string" ? e : "Failed to update status")
    }
  }

  const onSubmit = async (task: any) => {
    try {
      await backend.submitTask(orgId, facId, task._id, task.status)
      Toaster.success("Task submitted")
      loadTasks()
    } catch (e) {
      Toaster.error(typeof e === "string" ? e : "Failed to submit task")
    }
  }

  const onCancel = async (task: any) => {
    try {
      await backend.cancelTask(orgId, facId, task._id)
      Toaster.success("Task cancelled")
      loadTasks()
    } catch (e) {
      Toaster.error(typeof e === "string" ? e : "Failed to cancel task")
    }
  }

  // Assigner approves a submitted task (✓). Toggling off sends it back to the
  // "Review Completed Tasks" bucket.
  const onApprove = async (task: any) => {
    const next = task.check && task.check.mark === "yes" ? null : "yes"
    try {
      await backend.checkTask(orgId, facId, task._id, next)
      setTasks((ts) => ts.map((t) => (t._id === task._id ? { ...t, check: next ? { mark: next } : undefined } : t)))
    } catch (e) {
      Toaster.error(typeof e === "string" ? e : "Failed to approve task")
    }
  }

  // Assigner rejects a submitted task (✗): open the modal to capture a required
  // reason, then re-open the task back to the assignee's Assigned Tasks table.
  const onReject = (task: any) => {
    setRejectTask(task)
    setRejectReason("")
  }

  const confirmReject = async () => {
    if (!rejectReason.trim()) {
      Toaster.warning("Please enter a reason for rejection")
      return
    }
    setRejecting(true)
    try {
      await backend.checkTask(orgId, facId, rejectTask._id, "no", rejectReason.trim())
      Toaster.success("Task sent back to the assignee")
      setRejectTask(null)
      setRejectReason("")
      loadTasks()
    } catch (e) {
      Toaster.error(typeof e === "string" ? e : "Failed to send task back")
    } finally {
      setRejecting(false)
    }
  }

  const onCreate = async () => {
    if (!newTitle.trim() || !newAssignee) {
      Toaster.warning("Enter a task and choose a user")
      return
    }
    setSaving(true)
    try {
      await backend.createTask(orgId, facId, {
        title: newTitle.trim(),
        assignedTo: newAssignee,
        targetDate: newTarget ? moment(newTarget).toISOString() : undefined,
      })
      Toaster.success("Task created")
      setCreateOpen(false)
      setNewTitle(""); setNewAssignee(""); setNewTarget(null)
      loadTasks()
    } catch (e) {
      Toaster.error(typeof e === "string" ? e : "Failed to create task")
    } finally {
      setSaving(false)
    }
  }

  const downloadPending = async (scope: "to" | "by") => {
    try {
      await backend.downloadTasksPdf(orgId, facId, scope, "pending", `tasks-${scope}-pending.pdf`)
    } catch (e) {
      Toaster.error(typeof e === "string" ? e : "Failed to download PDF")
    }
  }

  const downloadCompleted = async (scope: "to" | "by") => {
    try {
      await backend.downloadTasksPdf(orgId, facId, scope, "completed", `tasks-${scope}-completed.pdf`, compRange[0], compRange[1])
    } catch (e) {
      Toaster.error(typeof e === "string" ? e : "Failed to download PDF")
    }
  }

  if (!selectedFactory) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="mw-empty-state">Select a factory to view assigned tasks.</div>
        </Container>
      </div>
    )
  }

  // ---- renderers ----
  // Window for tasks assigned TO the current user.
  const renderToMe = () => {
    const open = openOf(assignedToMe)
    const done = doneOf(assignedToMe)
    return (
      <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <Button color="success" onClick={() => downloadPending("to")}>Download PDF</Button>
        </div>
        <div className="table-responsive">
          <Table className="table mw-table table-bordered mb-0">
            <thead>
              <tr className="table-success">
                <th>S#</th><th style={{ minWidth: 220 }}>Tasks</th><th style={{ minWidth: 150 }}>Status</th>
                <th>Target Date</th><th>Assigned By</th><th>Assigned On</th><th>Submit</th><th>Check</th>
              </tr>
            </thead>
            <tbody>
              {open.length === 0 && <tr><td colSpan={8} className="text-center text-muted">No pending tasks assigned to you.</td></tr>}
              {open.map((t, i) => {
                const locked = t.status === "Cancelled"
                return (
                  <tr key={t._id}>
                    <td>{i + 1}</td>
                    <td>
                      {t.title}
                      {t.rejectionReason && (
                        <div style={{ marginTop: 4, fontSize: 12, color: "#c0392b" }}>
                          <strong>Sent back:</strong> {t.rejectionReason}
                        </div>
                      )}
                    </td>
                    <td>
                      {locked ? <StatusPill status="Cancelled" /> : (
                        <Input type="select" bsSize="sm" value={t.status || "Pending"} onChange={(e) => onStatusChange(t, e.target.value)}>
                          {ASSIGNEE_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </Input>
                      )}
                    </td>
                    <td>{fmtDT(t.targetDate)}</td>
                    <td>{t.assignedByName || nameOf(t.assignedBy)}</td>
                    <td>{fmtDT(t.createdAt)}</td>
                    <td>
                      {locked ? <span className="text-muted">—</span> :
                        <Button color="success" size="sm" onClick={() => onSubmit(t)}>Submit</Button>}
                    </td>
                    <td className="text-center"><CheckButtons task={t} interactive={false} onApprove={onApprove} onReject={onReject} /></td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
        {renderCompleted("to", done, showCompletedTo, setShowCompletedTo)}
      </>
    )
  }

  // Window for tasks assigned BY the current user. Three stages:
  //   Assigned Tasks      — open, not yet submitted (assignee is working on them)
  //   Review Completed     — submitted, awaiting the assigner's ✓ / ✗
  //   Completed Tasks      — approved with ✓ (collapsible history)
  const renderByMe = () => {
    const open = openOf(assignedByMe)
    const review = assignedByMe.filter((t) => t.submitted && !isApproved(t))
    const completed = assignedByMe.filter((t) => t.submitted && isApproved(t))
    return (
      <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <Button color="success" onClick={() => downloadPending("by")}>Download PDF</Button>
          {canAssign && <Button color="primary" onClick={() => setCreateOpen(true)}>+ Create Tasks</Button>}
        </div>
        <div className="table-responsive">
          <Table className="table mw-table table-bordered mb-0">
            <thead>
              <tr className="table-success">
                <th>S#</th><th style={{ minWidth: 220 }}>Tasks</th><th>Status</th>
                <th>Target Date</th><th>Assigned To</th><th>Assigned On</th><th></th>
              </tr>
            </thead>
            <tbody>
              {open.length === 0 && <tr><td colSpan={7} className="text-center text-muted">No open tasks assigned by you.</td></tr>}
              {open.map((t, i) => (
                <tr key={t._id}>
                  <td>{i + 1}</td>
                  <td>
                    {t.title}
                    {t.rejectionReason && (
                      <div style={{ marginTop: 4, fontSize: 12, color: "#c0392b" }}>
                        <strong>Sent back:</strong> {t.rejectionReason}
                      </div>
                    )}
                  </td>
                  <td><StatusPill status={t.status} /></td>
                  <td>{fmtDT(t.targetDate)}</td>
                  <td>{t.assignedToName || nameOf(t.assignedTo)}</td>
                  <td>{fmtDT(t.createdAt)}</td>
                  <td>
                    {t.status !== "Cancelled" &&
                      <Button color="link" size="sm" style={{ color: "#c0392b" }} onClick={() => onCancel(t)}>Cancel</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Review Completed Tasks — assignee submitted, awaiting ✓ / ✗. */}
        <div style={{ marginTop: 22 }}>
          <div style={{ fontWeight: 700, color: "#495057", marginBottom: 10 }}>
            REVIEW COMPLETED TASKS
            <small className="text-muted" style={{ marginLeft: 10, fontWeight: 400 }}>{review.length} to review</small>
          </div>
          <div className="table-responsive">
            <Table className="table mw-table table-bordered mb-0">
              <thead>
                <tr className="table-warning">
                  <th>S#</th><th style={{ minWidth: 220 }}>Tasks</th><th>Status</th>
                  <th>Target Date</th><th>Assigned To</th><th>Assigned On</th><th>Submitted On</th><th>Check</th>
                </tr>
              </thead>
              <tbody>
                {review.length === 0 && <tr><td colSpan={8} className="text-center text-muted">No tasks waiting for your review.</td></tr>}
                {review.map((t, i) => {
                  const tim = TIMING[t.timing]
                  return (
                    <tr key={t._id}>
                      <td>{i + 1}</td>
                      <td>{t.title}</td>
                      <td><StatusPill status={t.status} /></td>
                      <td>{fmtDT(t.targetDate)}</td>
                      <td>{t.assignedToName || nameOf(t.assignedTo)}</td>
                      <td>{fmtDT(t.createdAt)}</td>
                      <td>
                        {fmtDT(t.submittedOn)}
                        {tim && <div style={{ color: tim.color, fontWeight: 700, fontSize: 12 }}>{tim.label}</div>}
                      </td>
                      <td className="text-center"><CheckButtons task={t} interactive={true} onApprove={onApprove} onReject={onReject} /></td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        </div>

        {renderCompleted("by", completed, showCompletedBy, setShowCompletedBy)}
      </>
    )
  }

  const renderCompleted = (scope: "to" | "by", done: any[], open: boolean, setOpen: (v: boolean) => void) => (
    <div style={{ marginTop: 22 }}>
      <div style={{ display: "flex", alignItems: "center", cursor: "pointer", fontWeight: 700, color: "#495057" }}
        onClick={() => setOpen(!open)}>
        <span>COMPLETED TASKS ({scope === "to" ? "ASSIGNED TO ME" : "ASSIGNED BY ME"})</span>
        <i className={`mdi mdi-chevron-${open ? "up" : "down"}`} style={{ fontSize: 20, marginLeft: 6 }} />
        <small className="text-muted" style={{ marginLeft: 10, fontWeight: 400 }}>
          {done.length} completed
        </small>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
            <span className="text-muted" style={{ fontSize: 13 }}>Download completed (date range):</span>
            <Flatpickr className="form-control form-control-sm" style={{ maxWidth: 230 }}
              value={compRange} options={{ mode: "range", dateFormat: "Y-m-d" }}
              onChange={(d: any) => { if (d && d.length === 2) setCompRange([moment(d[0]).format("YYYY-MM-DD"), moment(d[1]).format("YYYY-MM-DD")]) }} />
            <Button color="success" size="sm" onClick={() => downloadCompleted(scope)}>Download PDF</Button>
          </div>
          <div className="table-responsive">
            <Table className="table mw-table table-bordered mb-0">
              <thead>
                <tr className="table-light">
                  <th>S#</th><th style={{ minWidth: 220 }}>Tasks</th><th>Status</th><th>Target Date</th>
                  <th>{scope === "to" ? "Assigned By" : "Assigned To"}</th><th>Assigned On</th><th>Submitted On</th><th>Check</th>
                </tr>
              </thead>
              <tbody>
                {done.length === 0 && <tr><td colSpan={8} className="text-center text-muted">No completed tasks yet.</td></tr>}
                {done.map((t, i) => {
                  const tim = TIMING[t.timing]
                  return (
                    <tr key={t._id}>
                      <td>{i + 1}</td>
                      <td>{t.title}</td>
                      <td><StatusPill status={t.status} /></td>
                      <td>{fmtDT(t.targetDate)}</td>
                      <td>{scope === "to" ? (t.assignedByName || nameOf(t.assignedBy)) : (t.assignedToName || nameOf(t.assignedTo))}</td>
                      <td>{fmtDT(t.createdAt)}</td>
                      <td>
                        {fmtDT(t.submittedOn)}
                        {tim && <div style={{ color: tim.color, fontWeight: 700, fontSize: 12 }}>{tim.label}</div>}
                      </td>
                      <td className="text-center"><CheckButtons task={t} interactive={scope === "by"} onApprove={onApprove} onReject={onReject} /></td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                  <h4 style={{ color: "#c0392b", margin: 0, fontWeight: 700 }}>TASKS ASSIGNED</h4>
                  <span className="text-muted">{selectedFactory.name}</span>
                </div>
                <Nav tabs className="nav-tabs-custom mt-3">
                  <NavItem>
                    <NavLink style={{ cursor: "pointer" }} className={classnames({ active: tab === "to" })} onClick={() => setTab("to")}>
                      Assigned To Me {pendingToCount > 0 && <Badge color="danger" pill className="ms-1">{pendingToCount}</Badge>}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink style={{ cursor: "pointer" }} className={classnames({ active: tab === "by" })} onClick={() => setTab("by")}>
                      Assigned By Me {pendingByCount > 0 && <Badge color="danger" pill className="ms-1">{pendingByCount}</Badge>}
                    </NavLink>
                  </NavItem>
                </Nav>
                <div className="mt-3">
                  {tab === "to" ? renderToMe() : renderByMe()}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={createOpen} toggle={() => setCreateOpen(!createOpen)}>
        <ModalHeader toggle={() => setCreateOpen(!createOpen)}>Create Task</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Task</Label>
            <Input type="textarea" rows={2} value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Describe the task" />
          </FormGroup>
          <FormGroup>
            <Label>Assign To</Label>
            <Input type="select" value={newAssignee} onChange={(e) => setNewAssignee(e.target.value)}>
              <option value="">Select user</option>
              {assignableUsers.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Input>
            {assignableUsers.length === 0 && <small className="text-danger">No users configured for you to assign to.</small>}
          </FormGroup>
          <FormGroup>
            <Label>Target Date</Label>
            <Flatpickr className="form-control" value={newTarget}
              options={{ enableTime: true, dateFormat: "Y-m-d H:i" }}
              onChange={(d: any) => setNewTarget(d && d[0] ? d[0] : null)} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button color="primary" onClick={onCreate} disabled={saving}>{saving ? "Saving..." : "Create"}</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!rejectTask} toggle={() => !rejecting && setRejectTask(null)}>
        <ModalHeader toggle={() => !rejecting && setRejectTask(null)}>Reject Task</ModalHeader>
        <ModalBody>
          <div className="text-muted mb-2" style={{ fontSize: 13 }}>
            This task will be sent back to <strong>{rejectTask && (rejectTask.assignedToName || nameOf(rejectTask.assignedTo))}</strong> to redo and resubmit.
          </div>
          <FormGroup>
            <Label>Reason for rejection <span className="text-danger">*</span></Label>
            <Input type="textarea" rows={3} value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain what needs to be corrected" />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setRejectTask(null)} disabled={rejecting}>Cancel</Button>
          <Button color="danger" onClick={confirmReject} disabled={rejecting}>{rejecting ? "Sending..." : "Send back"}</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

const mapStateToProps = (state: any) => {
  const { user } = state.user
  const { selected: selectedFactory } = state.factory
  return { selectedFactory, user }
}

export default withRouter(connect(mapStateToProps, {})(TasksAssigned))
